import { type FC } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { Select, SelectContent, SelectItem } from '@packages/shared/src/ui/Select/Select';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { DisplaySettingsDesktopPaginationFragmentFragmentDoc } from '@packages/gql/generated/shopping/DisplaySettingsDesktopPaginationFragmentFragmentDoc';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { usePaging } from '../../hooks/usePaging';

/* GraphQL */ `
  fragment DisplaySettingsDesktopPaginationFragment on SearchProductResult {
    ...UsePagingFragment
  }
`;

export type DisplaySettingsDesktopPaginationProps = {
  maskedData: FragmentType<typeof DisplaySettingsDesktopPaginationFragmentFragmentDoc>;
  /** optional className */
  className?: string;
};
/**
 * DisplaySettingsDesktopPagination component description displayed in storybook
 * */

const messages = defineMessages({
  selectLabel: {
    id: 'search.productList.pagination.label',
    defaultMessage: 'Seite',
  },
  nextPage: {
    id: 'search.productList.pagination.next.label',
    defaultMessage: 'Nächste Seite',
  },
  previousPage: {
    id: 'search.productList.pagination.previous.label',
    defaultMessage: 'Vorherige Seite',
  },
});

export const DisplaySettingsDesktopPagination: FC<DisplaySettingsDesktopPaginationProps> = ({
  maskedData,
  className,
}) => {
  const data = unmask<typeof DisplaySettingsDesktopPaginationFragmentFragmentDoc>(maskedData);

  const { formatMessage } = useIntl();
  const { pageCount, currentPage, pushPageChange } = usePaging(data);
  const pageCountItems = Array.from({ length: pageCount }, (_, index) => ({
    value: index + 1,
    label: formatMessage(
      {
        id: 'search.productList.pagination.option',
        defaultMessage: '{current} von {count}',
      },
      {
        current: index + 1,
        count: pageCount,
      },
    ),
  }));

  return (
    <div
      className={`grid items-end gap-0 [grid-template:'arrow1_select_arrow2'_auto_/min-content_auto_min-content] [&_div]:[grid-area:select] ${className}`}
    >
      <Button
        disabled={currentPage === 1}
        variant="icon-only"
        aria-label={formatMessage(messages.previousPage)}
        onClick={() => {
          pushPageChange?.(currentPage - 1, true);
        }}
        title={formatMessage(messages.previousPage)}
        className="border-color-transparent bg-color-transparent enabled:focus-visible:bg-color-transparent mb-0.5 [grid-area:arrow1]"
      >
        <span className="icon-emp-arrow-medium-left size-4" />
      </Button>
      <Select
        value={String(currentPage)}
        onValueChange={(event) => {
          pushPageChange?.(Number(event), true);
        }}
        id={formatMessage(messages.selectLabel).toLowerCase()}
        label={formatMessage(messages.selectLabel)}
      >
        <SelectContent sideOffset={-8} className="z-modal">
          {pageCountItems.map(({ value, label }) => (
            <SelectItem key={value} value={value.toString()}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        disabled={currentPage === pageCount}
        variant="icon-only"
        aria-label={formatMessage(messages.nextPage)}
        onClick={() => {
          pushPageChange?.(currentPage + 1, true);
        }}
        title={formatMessage(messages.nextPage)}
        className="border-color-transparent bg-color-transparent enabled:focus-visible:bg-color-transparent mb-0.5 [grid-area:arrow2]"
      >
        <span className="icon-emp-arrow-medium-right size-4" />
      </Button>
    </div>
  );
};
